// import Hotspot from "@/templates/Heritage/variants/default/features/hotspot/hotspot";

import { GET } from "@/app/api/health/route";
import subLink from "../../features/subLink/subLink";

// Helper function to add id_path parameter to project endpoints
const withIdPath = (endpoint: string, id_path?: string) => {
    const separator = endpoint.includes('?') ? '&' : '?';
    return id_path ? `${endpoint}${separator}id_path=${encodeURIComponent(id_path)}` : endpoint;
};

const apiEndPointList = {
    auth: {
        LOGIN: (id_path?: string) => withIdPath("/v1/projects/users/login", id_path),
        VERIFY_OTP: (id_path?: string) => withIdPath("/v1/projects/users/verify-otp", id_path),
        REGISTER: (id_path?: string) => withIdPath("/v1/projects/users", id_path),
        LOGOUT: (id_path?: string) => withIdPath("/v1/projects/users/logout", id_path),
        VERIFY_EMAIL: (id_path?: string) => withIdPath("/v1/projects/users/verify-email", id_path),
        REFRESH_ACCESS_TOKEN: (id_path?: string) => withIdPath("/v1/projects/users/refresh-token", id_path),
        VERIFY_ACCOUNT: (id_path?: string) => withIdPath("/v1/projects/users/verify-email", id_path),
        FORGOT_PASSWORD: (id_path?: string) => withIdPath("/v1/projects/users/forgot-password", id_path),
    },

    projectMenu: {
        GET_TREE: (id_path?: string) => withIdPath("/v1/projects/menu/tree", id_path),
        SAVE_TREE: (id_path?: string) => withIdPath("/v1/projects/menu/tree", id_path),
        GET_LIST: (id_path?: string) => withIdPath("/v1/projects/menu/list", id_path),
        GET_ITEM: (id: string, id_path?: string) => withIdPath(`/v1/projects/menu/item/${id}`, id_path),
        GET_BY_ID: (id: string, id_path?: string) => withIdPath(`/v1/projects/menu/item/${id}`, id_path),
        CREATE_ITEM: (id_path?: string) => withIdPath("/v1/projects/menu/item", id_path),
        UPDATE_ITEM: (id: string, id_path?: string) => withIdPath(`/v1/projects/menu/item/${id}`, id_path),
        REORDER: (id_path?: string) => withIdPath("/v1/projects/menu/reorder", id_path),
        DELETE_ITEM: (id: string, id_path?: string) => withIdPath(`/v1/projects/menu/item/${id}`, id_path),
        RESET: (id_path?: string) => withIdPath("/v1/projects/menu/reset", id_path),
    },

    generalLanguage: {
        GET_ALL_LANGUAGES: "/v1/general/languages",
        UPDATE_LANGUAGE: (id: string) => `/v1/general/languages/${id}`,
        SYNC_LANGUAGES: "/v1/general/languages/sync",
        GET_MESSAGES: (locale: string) => `/v1/general/languages/messages?locale=${locale}`,
        UPSERT_MESSAGES: (locale: string) => `/v1/general/languages/messages/${locale}`,
        AUTO_TRANSLATE_MESSAGES: "/v1/general/languages/messages/auto-translate",
        ESTIMATE_TRANSLATION: "/v1/general/languages/translation/estimate",
        GET_TRANSLATION_BUDGET: (projectId: number | string = 0) => `/v1/general/languages/translation/budget?project_id=${projectId}`,
        UPSERT_TRANSLATION_BUDGET: "/v1/general/languages/translation/budget",
        GET_TRANSLATION_USAGE_LOGS: (projectId: number | string = 0) => `/v1/general/languages/translation/usage-logs?project_id=${projectId}&limit=20`
    },

    passwordManagement: {
        FORGOT_PASSWORD: (id_path?: string) => withIdPath("/v1/projects/password-management/forgot-password", id_path),
        RESET_PASSWORD: (id_path?: string) => withIdPath("/v1/projects/password-management/reset-password", id_path),
        CHANGE_PASSWORD: (id_path?: string) => withIdPath("/v1/projects/password-management/change-password", id_path),
    },


    cmc: {
        UPLOAD_TO_S3: (id_path?: string) => `/v1/projects/cmc/upload?id_path=${id_path}`,
        LIST_OBJECTS: () => `/v1/general/cmc/list-objects`,
        DELETE_OBJECT: () => `/v1/general/cmc/delete-object`,
        DELETE_OBJECTS: (id_path?: string) => `/v1/projects/projects/cmc/delete?id_path=${id_path}`,
        CREATE_FOLDER: (id_path?: string) => {
        if (id_path) {
            return `/v1/projects/cmc/create-folder?id_path=${id_path}`;
        }
        return `/v1/general/cmc/create-folder`;
    },
        READ_FILE: () => `/v1/general/cmc/read-file`,
        GET_PRESIGNED_URL: (id_path?: string) => `/v1/projects/projects/cmc/get-presigned-url?id_path=${id_path}`,
        SEARCH: () => `/v1/general/cmc/search`
    },

    language: {
        ADD_LANGUAGE: (id_path?: string) => `/v1/projects/language/?id_path=${id_path}`,
        GET_LANGUAGE_LIST: (id_path: string) => `/v1/projects/language?id_path=${id_path}`,
        GET_LANGUAGE_BY_ID: (id_path: string, id: number) => `/v1/projects/language/vn?id_path=${id_path}&id=${id}`,
        UPDATE_LANGUAGE: (id: string, id_path: string) => `/v1/projects/language/${id}?id_path=${id_path}`,
        UPDATE_DEFAULT_LANGUAGE: (id_path: string) => `/v1/projects/language/default?id_path=${id_path}`,
        DELETE_LANGUAGE: (id: string, id_path: string) => `/v1/projects/language/${id}?id_path=${id_path}`,
        GET_GOOGLE_SUPPORTED_LANGUAGES: (id_path: string) => `/v1/projects/language/google-supported?id_path=${id_path}`,
        AUTO_TRANSLATE: (id: string, id_path: string) => `/v1/projects/language/${id}/auto-translate?id_path=${id_path}`,
        ESTIMATE_TRANSLATE: (id: string, id_path: string) => `/v1/projects/language/${id}/estimate-translate?id_path=${id_path}`,
        GET_CURRENT_TRANSLATION_JOB: (id: string, id_path: string) => `/v1/projects/language/${id}/translation-job/current?id_path=${id_path}`,
        GET_TRANSLATION_JOB_STATUS: (id: string, jobId: string, id_path: string) => `/v1/projects/language/${id}/translation-job/${jobId}?id_path=${id_path}`
    },

    GoogleMap: {
        ADD_GOOGLEMAP: (id_path: string) => `/v1/projects/google-map?id_path=${id_path}`,
        GET_ALL_GOOGLEMAP: (id_path: string) => `/v1/projects/google-map?id_path=${id_path}`,
        GET_GOOGLEMAP_BY_ID: (id_path: string, id: number) => `/v1/projects/google-map/${id}?id_path=${id_path}`,
        UPDATE_GOOGLEMAP: (id_path: string, id: number) => `/v1/projects/google-map/${id}?id_path=${id_path}`,
        DELETE_GOOGLEMAP: (id_path: string, id: number) => `/v1/projects/google-map/${id}?id_path=${id_path}`,

        GET_GOOGLE_MAP_KEY: (id_path: string) => withIdPath(`/v1/general/google/google-api-key`, id_path),

        GET_LOCATION_BY_ID: (id_path: string, id?: string, language_id?: string) =>
            withIdPath(`/v1/projects/google-map-locations/${id}?language_id=${language_id}`, id_path),
        ADD_GOOGLEMAP_LOCATION: (id_path: string) =>
            withIdPath(`/v1/projects/google-map-locations`, id_path),
        UPDATE_GOOGLEMAP_LOCATION: (id_path: string, id: string | number) =>
            withIdPath(`/v1/projects/google-map-locations/${id}`, id_path),
        DELETE_GOOGLEMAP_LOCATION: (id_path: string, id: string | number) =>
            withIdPath(`/v1/projects/google-map-locations/${id}`, id_path),
    },

    minimap: {
        GET_ALL_MINIMAPS: (id_path: string, page: number, limit: number, search?: string) => {
            let url = `/v1/projects/minimap?id_path=${id_path}&page=${page}&limit=${limit}`;
            if (search) url += `&search=${search}`;
            return url;
        },
        GET_MAIN_MINIMAPS: (id_path: string, page: number, limit: number, search?: string) => {
            let url = `/v1/projects/minimap/mainmap?id_path=${id_path}&page=${page}&limit=${limit}`;
            if (search) url += `&search=${search}`;
            return url;
        },
        GET_ALL_LOCATIONS: (id_path: string, page: number, limit: number, search?: string) => {
            let url = `/v1/projects/minimap/locations?id_path=${id_path}&page=${page}&limit=${limit}`;
            if (search) url += `&search=${search}`;
            return url;
        },
        GET_MINIMAP_BY_ID: (id_path: string, id: number, language_id?: string) =>
            `/v1/projects/minimap/${id}?id_path=${id_path}${language_id ? `&language_id=${language_id}` : ''}`,
        GET_MINIMAP_WITH_LOCATIONS: (id_path, id, language_id) =>
            `/v1/projects/minimap/${id}/locations?id_path=${id_path}${language_id ? `&language_id=${language_id}` : ''}`,
        GET_LOCATION_BY_ID: (id_path: string, id: number, language_id?: string) =>
            `/v1/projects/minimap/locations/${id}?id_path=${id_path}${language_id ? `&language_id=${language_id}` : ''}`,

        CREATE_MINIMAP: (id_path: string) => `/v1/projects/minimap?id_path=${id_path}`,
        UPDATE_MINIMAP: (id_path: string, id: number) => `/v1/projects/minimap/${id}?id_path=${id_path}`,
        DELETE_MINIMAP: (id_path: string, id: number) => `/v1/projects/minimap/${id}?id_path=${id_path}`,

        ADD_LOCATION: (id_path: string) => `/v1/projects/minimap/locations?id_path=${id_path}`,
        UPDATE_LOCATION: (id_path: string, id: number) => `/v1/projects/minimap/locations/${id}?id_path=${id_path}`,
        DELETE_LOCATION: (id_path: string, id: number) => `/v1/projects/minimap/locations/${id}?id_path=${id_path}`,

        // Visibility (Public/Private) bulk update endpoints
        UPDATE_ALL_LOCATIONS_VISIBILITY: (id_path: string, minimap_id: number) => `/v1/projects/minimap/${minimap_id}/locations/visibility?id_path=${id_path}`,
        UPDATE_LOCATIONS_VISIBILITY_BULK: (id_path: string) => `/v1/projects/minimap/locations/visibility/bulk?id_path=${id_path}`,

        // Scale bulk update endpoint
        UPDATE_ALL_LOCATIONS_SCALE: (id_path: string, minimap_id: number) => `/v1/projects/minimap/${minimap_id}/locations/scale?id_path=${id_path}`,

        // Marker type bulk update endpoint
        UPDATE_ALL_LOCATIONS_MARKER_TYPE: (id_path: string, minimap_id: number) => `/v1/projects/minimap/${minimap_id}/locations/marker-type?id_path=${id_path}`,
    },

    minimapGroup: {
        GET_GROUPS: (id_path: string, minimap_id?: number, language?: string) => {
            let url = `/v1/projects/minimap/groups/all?id_path=${id_path}`;
            if (minimap_id) url += `&minimap_id=${minimap_id}`;
            if (language) url += `&language=${language}`;
            return url;
        },
        GET_GROUP_BY_ID: (id_path: string, id: number, language?: string) => {
            let url = `/v1/projects/minimap/groups/${id}?id_path=${id_path}`;
            if (language) url += `&language=${language}`;
            return url;
        },
        GET_LOCATION_GROUP_MAPPINGS: (id_path: string, minimap_id: number) =>
            `/v1/projects/minimap/groups/location-mappings?id_path=${id_path}&minimap_id=${minimap_id}`,
        CREATE_GROUP: (id_path: string) => `/v1/projects/minimap/groups?id_path=${id_path}`,
        UPDATE_GROUP: (id_path: string, id: number) => `/v1/projects/minimap/groups/${id}?id_path=${id_path}`,
        DELETE_GROUP: (id_path: string, id: number) => `/v1/projects/minimap/groups/${id}?id_path=${id_path}`,
        GET_NESTED_GROUPS_WITH_MARKERS: (id_path: string, minimap_id: number, language_id?: string) => {
            let url = `/v1/projects/minimap/groups/nested?id_path=${id_path}&minimap_id=${minimap_id}`;
            if (language_id) url += `&language_id=${language_id}`;
            return url;
        },
        ADD_LOCATION_TO_GROUP: (id_path: string, group_id: number, location_id: number) =>
            `/v1/projects/minimap/groups/${group_id}/locations/${location_id}?id_path=${id_path}`,
        REMOVE_LOCATION_FROM_GROUP: (id_path: string, group_id: number, location_id: number) =>
            `/v1/projects/minimap/groups/${group_id}/locations/${location_id}?id_path=${id_path}`,
    },

    minimapPolygon: {
        // Get all polygons for a minimap
        GET_POLYGONS_BY_MINIMAP: (id_path: string, minimap_id: number, language_id?: string) => {
            let url = `/v1/projects/minimap/${minimap_id}/polygons?id_path=${id_path}`;
            if (language_id) url += `&language_id=${language_id}`;
            return url;
        },
        // Single polygon CRUD
        GET_POLYGON_BY_ID: (id_path: string, id: number, language_id?: string) => {
            let url = `/v1/projects/minimap/polygons/${id}?id_path=${id_path}`;
            if (language_id) url += `&language_id=${language_id}`;
            return url;
        },
        CREATE_POLYGON: (id_path: string) => `/v1/projects/minimap/polygons?id_path=${id_path}`,
        UPDATE_POLYGON: (id_path: string, id: number) => `/v1/projects/minimap/polygons/${id}?id_path=${id_path}`,
        DELETE_POLYGON: (id_path: string, id: number) => `/v1/projects/minimap/polygons/${id}?id_path=${id_path}`,
        // Visibility bulk update
        UPDATE_ALL_POLYGONS_VISIBILITY: (id_path: string, minimap_id: number) => `/v1/projects/minimap/${minimap_id}/polygons/visibility?id_path=${id_path}`,
        UPDATE_POLYGONS_VISIBILITY_BULK: (id_path: string) => `/v1/projects/minimap/polygons/visibility/bulk?id_path=${id_path}`,
    },

    admin: {
        //Auto Tour
        GET_ALL_AUTOTOUR: (id_path: string, page: number, limit: number) => `/v1/projects/autotour?id_path=${id_path}&page=${page}&limit=${limit}`,
        CREATE_AUTOTOUR: (id_path: string) => `/v1/projects/autotour?id_path=${id_path}`,
        DUPLICATE_AUTOTOUR: (id_path: string, autotour_id: number) => `/v1/projects/autotour/${autotour_id}/duplicate?id_path=${encodeURIComponent(id_path)}`,
        DELETE_AUTOTOUR: (id_path: string, autotour_id: number) => `/v1/projects/autotour/${autotour_id}?id_path=${id_path}`,
        GET_AUTOTOUR_DETAIL_BY_ID: (id_path: string, autotour_id: number) => `/v1/projects/autotour/${autotour_id}?id_path=${id_path}`,
        UPDATE_AUTOTOUR: (id_path: string, autotour_id: number) => `/v1/projects/autotour/${autotour_id}?id_path=${id_path}`,
        UPDATE_AUTOTOUR_VISIBILITY: (id_path: string, autotour_id: number | string) => `/v1/projects/autotour/${autotour_id}/visibility?id_path=${encodeURIComponent(id_path)}`,

        //Category
        GET_ALL_CATEGORIES: (id_path: string) => `/v1/projects/category?id_path=${id_path}`,
        GET_CATEGORY_HIERARCHY_WITH_ASSETS: (id_path: string) => `/v1/projects/asset/category-hierarchy-with-assets?id_path=${id_path}`,
        GET_CATEGORY_CONTENTS: (
            id_path: string,
            uuid?: string,
            page?: number,
            limit?: number,
            includeRootAssets = false,
            includeSubcategories = true,
        ) => {
            const paginationParams = page && limit ? `&page=${page}&limit=${limit}` : '';
            const rootAssetsParam = includeRootAssets ? '&include_root_assets=true' : '';
            const subcategoriesParam = includeSubcategories ? '' : '&include_subcategories=false';
            return uuid ? `/v1/projects/asset/category/${uuid}/contents?id_path=${id_path}${paginationParams}${rootAssetsParam}${subcategoriesParam}` : `/v1/projects/asset/category/root/contents?id_path=${id_path}${paginationParams}${rootAssetsParam}${subcategoriesParam}`;
        },
        GET_CATEGORY_BY_ID: (id_path: string, uuid: string, language_id?: string) => `/v1/projects/category/${uuid}?id_path=${id_path}${language_id ? `&language_id=${language_id}` : ''}`,
        ADD_NEW_CATEGORY: (id_path: string) => `/v1/projects/category?id_path=${id_path}`,
        DELETE_CATEGORY: (id: string, id_path: string) => `/v1/projects/category/${id}?id_path=${id_path}`,
        UPDATE_CATEGORY: (id: string, id_path: string) => `/v1/projects/category/${id}?id_path=${id_path}`,
        GET_CATEGORY_COUNTS: (id_path: string, category_uuid: string) => `/v1/projects/asset/category/${category_uuid}/counts?id_path=${id_path}`,

        //Asset
        GET_ASSET_BY_CATEGORY: (id_path: string, category_id: string) => `/v1/projects/asset/category/${category_id}?id_path=${id_path}`,
        CREATE_NEW_ASSET: (id_path: string) => `/v1/projects/asset?id_path=${id_path}`,
        CREATE_ASSET_VARIABLE: (id_path: string) => `/v1/projects/asset/variables?id_path=${id_path}`,
        LIST_ASSET_APPROVAL_REQUESTS: (id_path: string) => `/v1/projects/asset/approval-requests?id_path=${id_path}`,
        CREATE_ASSET_APPROVAL_REQUEST: (id_path: string) => `/v1/projects/asset/approval-requests?id_path=${id_path}`,
        APPROVE_ASSET_APPROVAL_REQUEST: (id_path: string, requestId: number) => `/v1/projects/asset/approval-requests/${requestId}/approve?id_path=${id_path}`,
        REJECT_ASSET_APPROVAL_REQUEST: (id_path: string, requestId: number) => `/v1/projects/asset/approval-requests/${requestId}/reject?id_path=${id_path}`,
        CANCEL_ASSET_APPROVAL_REQUEST: (id_path: string, requestId: number) => `/v1/projects/asset/approval-requests/${requestId}/cancel?id_path=${id_path}`,
        MY_PENDING_APPROVAL_SYSTEM_REQUESTS: (id_path: string) => `/v1/projects/approval-system/my-pending?id_path=${id_path}`,

        GET_ASSET_BY_TYPE: (id_path: string, type: string) => `/v1/projects/asset/type/${type}?id_path=${id_path}`,
        GET_CATEGORY_BY_ASSET_TYPE_AND_LEVEL: (id_path: string, type: string, level: any, language_id: string) => `/v1/projects/asset/type/${type}/categories?id_path=${id_path}&level=${level}&language_id=${language_id}`,
        GET_ALL_ASSETS: (id_path: string, page?: number, limit?: number, search?: string, type?: string, sortBy?: string, types?: string, statuses?: string) => {
    let url = `/v1/projects/asset?id_path=${id_path}`;
    if (page) url += `&page=${page}`;
    if (limit) url += `&limit=${limit}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (type) url += `&type=${encodeURIComponent(type)}`;
    if (sortBy) url += `&sortBy=${encodeURIComponent(sortBy)}`;
    if (types) url += `&types=${encodeURIComponent(types)}`;
    if (statuses) url += `&statuses=${encodeURIComponent(statuses)}`;
    return url;
  },
        GET_ASSET_BY_ID: (id_path: string, id: string) => `/v1/projects/asset/${id}?id_path=${id_path}`,
        GET_ASSET_WITH_TYPE: (id_path: string) => `/v1/projects/asset/grouped-by-type?id_path=${id_path}`,
        EXPORT_EXCEL: (id_path: string) => `/v1/projects/asset/export-excel?id_path=${id_path}`,
        EXPORT_EDIT_EXCEL: (id_path: string, category_uuid?: string, asset_type_id?: string | number, uuids?: string) => {
            let url = `/v1/projects/asset/export/edit-excel?id_path=${id_path}`;
            if (category_uuid) url += `&category_uuid=${encodeURIComponent(category_uuid)}`;
            if (asset_type_id !== undefined && asset_type_id !== null && asset_type_id !== '') url += `&asset_type_id=${encodeURIComponent(String(asset_type_id))}`;
            if (uuids) url += `&uuids=${encodeURIComponent(uuids)}`;
            return url;
        },
        IMPORT_EDIT_EXCEL: (id_path: string) => `/v1/projects/asset/import/edit-excel?id_path=${id_path}`,
        // Hotspot endpoints


        // S3 upload
        UPLOAD_TO_S3: (id_path: string) => `/v1/projects/cmc/upload?id_path=${id_path}`,

        // video group
        CREATE_NEW_VIDEO_GROUP: (id_path: string) => `/v1/projects/video-group?id_path=${id_path}`,
        GET_ALL_VIDEO_GROUPS: (id_path: string) => `/v1/projects/video-group?id_path=${id_path}`,
        GET_ALL_GROUP_WITHOUT_VIDEO_GROUP: (id_path: string) => `/v1/projects/video-group/groups-without-videos?id_path=${id_path}`,
        UPDATE_VIDEO_GROUP: (id_path: string, group_id: number) => `/v1/projects/video-group/${group_id}?id_path=${id_path}`,
        DELETE_VIDEO_GROUP: (id_path: string, group_id: number) => `/v1/projects/video-group/${group_id}?id_path=${id_path}`,
    },

    Group: {
        GET_ALL_GROUP: (id_path: string, language_id: string, limit: number, page: number) => `/v1/projects/group?language_id=${language_id}&limit=${limit}&page=${page}&id_path=${id_path}`,
        GET_GROUP_BY_ID: (id_path: string, group_id: number, language_id: string) => `/v1/projects/group/${group_id}?language_id=${language_id}&id_path=${id_path}`,
        GET_ALL_GROUP_NESTED: (id_path: string) => `/v1/projects/group/nested?id_path=${id_path}`,
        GET_ALL_PARENT_GROUP: (
            id_path: string,
            language_id: string,
            version_id: number,
            page?: number,
            limit?: number,
            is_show?: number | string,
            scene_name?: string
        ) => {
            let url = `/v1/projects/group/parents?language_id=${language_id}&version_id=${version_id}&id_path=${id_path}`;
            if (page) url += `&page=${page}`;
            if (limit) url += `&limit=${limit}`;
            if (is_show !== undefined && is_show !== null) url += `&is_show=${is_show}`;
            if (scene_name) url += `&scene_name=${encodeURIComponent(scene_name)}`;
            return url;
        },
        GET_ALL_CHILD_GROUP_BY_GROUP: (id_path: string, group_id: number) => `/v1/projects/group/${group_id}/children?id_path=${id_path}`,
        SEARCH_GROUP_ADVANCED: (id_path: string, keyword: string, parent_group_id: string, language_id: string, version_id: number, page: number, limit: number) => `/v1/projects/group/search?keyword=${encodeURIComponent(keyword)}&parent_group_id=${parent_group_id}&language_id=${language_id}&version_id=${version_id}&page=${page}&limit=${limit}&id_path=${id_path}`,
        SEARCH_GROUP: (id_path: string, search: string) => `/v1/projects/group/search/${search}?id_path=${id_path}`,
        INSERT_NEW_GROUP: (id_path: string) => `/v1/projects/group?id_path=${id_path}`,
        ADD_GROUP_INTO_GROUP: (id_path: string, group_id: number) => `/v1/projects/group-scene/add-groups/${group_id}?id_path=${id_path}`,
        DELETE_GROUP: (id_path: string, group_id: number) => `/v1/projects/group/${group_id}?id_path=${id_path}`,
        ADD_VOICE_FOR_GROUP: (id_path: string, group_id: number) => `/v1/projects/group/${group_id}/voice?id_path=${id_path}`,
        ADD_VOICES_FOR_GROUP: (id_path: string, group_id: number) => `/v1/projects/group/${group_id}/voices?id_path=${id_path}`,
        UPDATE_GROUP: (id_path: string, group_id: number) => `/v1/projects/group/${group_id}?id_path=${id_path}`,
        APPLY_PARALLAX_TO_ALL: (id_path: string) => `/v1/projects/group/parallax/all?id_path=${id_path}`,
        EXPORT_GROUP_EXCEL: (id_path: string, version_id?: number) => `/v1/projects/export/group-excel?id_path=${id_path}${version_id ? `&version_id=${version_id}` : ''}`,
        IMPORT_GROUP_EXCEL: (id_path: string) => `/v1/projects/import/group-excel?id_path=${id_path}`,
    },

    GroupScene: {
        GET_ALL_GROUP_SCENE_NESTED: (id_path: string, language_id: string, version_id: number, limit: number, page: number) => `/v1/projects/group-scene/nested/${language_id}?version_id=${version_id}&limit=${limit}&page=${page}&id_path=${id_path}`,
        GET_LIST_SCENE_OF_GROUP: (id_path: string, group_id: number, language_id: string) => `/v1/projects/group-scene/scenes/${group_id}/${language_id}?id_path=${id_path}`,
        GET_LIST_SCENES_WITHOUT_GROUP: (id_path: string) => `/v1/projects/group-scene/scenes-without-group/vn?id_path=${id_path}`,
        ADD_SSCENE_INTO_GROUP: (id_path: string, group_id: number) => `/v1/projects/group-scene/${group_id}?id_path=${id_path}`,
        REMOVE_GROUP_OUT_GROUP: (id_path: string, group_scene_id: number) => `/v1/projects/group-scene/remove-groups/${group_scene_id}?id_path=${id_path}`,
        REMOVE_SCENE_FROM_GROUP: (id_path: string, group_id: number) => `/v1/projects/group-scene/remove/${group_id}?id_path=${id_path}`,
        // Scene reorder APIs
        REORDER_SCENES: (id_path: string, group_id: number) => `/v1/projects/group-scene/reorder/${group_id}?id_path=${id_path}`,
        SWAP_SCENES: (id_path: string, group_id: number) => `/v1/projects/group-scene/swap/${group_id}?id_path=${id_path}`,
        MOVE_SCENE_TO_INDEX: (id_path: string, group_id: number) => `/v1/projects/group-scene/move/${group_id}?id_path=${id_path}`,
        GET_GROUPS_ONLY: (id_path: string, language_id: string) => `/v1/projects/group-scene/only/all/${language_id}?id_path=${id_path}`,
    },

    GroupVersion: {
        GET_ALL_GROUP_VERSION: (id_path: string, limit: number, page: number) => `/v1/projects/group-versions?limit=${limit}&page=${page}&id_path=${id_path}`,
        GET_GROUP_VERSION_BY_ID: (id_path: string, id: number) => `/v1/projects/group-versions/${id}?id_path=${id_path}`,
        ADD_NEW_GROUP_VERSION: (id_path: string) => `/v1/projects/group-versions?id_path=${id_path}`,
        UPDATE_GROUP_VERSION_BY_ID: (id_path: string, id: number) => `/v1/projects/group-versions/${id}?id_path=${id_path}`,
        DELETE_GROUP_VERSION_BY_ID: (id_path: string, id: number) => `/v1/projects/group-versions/${id}?id_path=${id_path}`,
    },

    Scene: {
        GET_LIST_SCENES: ({ id_path, page, limit, language_id, is_show }: { id_path: any, page: any, limit: any, language_id: string, is_show?: any }) => `/v1/projects/scene/?page=${page}&limit=${limit}&language_id=${language_id}&id_path=${id_path}${is_show ? `&is_show=${is_show}` : ''}`,
        GET_LIST_SCENES_WITHOUT_GROUP: (id_path: string, language_id: string) => `/v1/projects/group-scene/scenes-without-group/${language_id}?id_path=${id_path}`,
        GET_SCENES_ID: (id_path: string, id_scene: number, language_id: string, gender: string) => `/v1/projects/scene/${id_scene}?language_id=${language_id}&gender=${gender}&id_path=${id_path}`,
        GET_SCENES_BY_NAME: (id_path: string, name: string, language_id: string, gender: string) => `/v1/projects/scene/name/${encodeURIComponent(name)}?language_id=${language_id}&gender=${gender}&id_path=${id_path}`,
        GET_ALL_VOICE_FOR_SCENE: (id_path: string, scene_name: string) => `/v1/projects/voice/scene/${scene_name}/all?id_path=${id_path}`,
        SEARCH_SCENE: (id_path: string, search: string, language_id: string) => `/v1/projects/scene/search/${search}?language_id=${language_id}&id_path=${id_path}`,
        ADD_SINGLE_SCENE: (id_path: string) => `/v1/projects/scene?id_path=${id_path}`,
        ADD_ALL_NEW_SCENE: (id_path: string) => `/v1/projects/scene/batch?id_path=${id_path}`,
        EXPORT_ADMIN_SCENES_EXCEL: (id_path: string) => `/api/admin/scenes/export?id_path=${id_path}`,
        IMPORT_ADMIN_SCENES_EXCEL: (id_path: string) => `/v1/projects/import/scene-excel?id_path=${id_path}`,
        UPDATE_SCENE_BY_ID: (id_path: string, id_scene: number) => `/v1/projects/scene/${id_scene}?id_path=${id_path}`,
        UPDATE_SCENE_BY_NAME: (id_path: string, name: string) => `/v1/projects/scene/name/${encodeURIComponent(name)}?id_path=${id_path}`,
        ADD_VOICE_FOR_SCENE: (id_path: string, scene_id: number) => `/v1/projects/scene/${scene_id}/voice?id_path=${id_path}`,
        ADD_VOICES_FOR_SCENE: (id_path: string, scene_id: number) => `/v1/projects/scene/${scene_id}/voices?id_path=${id_path}`,

        GET_PREVIOUS_SCENE_BY_SCENE_NAME: (id_path: string, sceneName: string, language_id: string) => `/v1/projects/scene/name/${encodeURIComponent(sceneName)}/previous?language_id=${language_id}&id_path=${id_path}`,
        GET_NEXT_SCENE_BY_SCENE_NAME: (id_path: string, sceneName: string, language_id: string) => `/v1/projects/scene/name/${encodeURIComponent(sceneName)}/next?language_id=${language_id}&id_path=${id_path}`,

    },

    Hotspot: {
        GET_ALL_HOTSPOTS: (id_path: string) => `/v1/projects/hotspot?id_path=${id_path}`,
        GET_HOTSPOTS_BY_SCENE_NAME: (id_path: string, scene_name: string) => `/v1/projects/hotspot/scene-name/${scene_name}?id_path=${id_path}`,
        GET_HOTSPOT_BY_ID: (id_path: string, hotspot_id: number) => `/v1/projects/hotspot/${hotspot_id}?id_path=${id_path}`,
        GET_HOTSPOT_STYLE: () => `/v1/general/hotspot-types`,
        GET_HOTSPOT_STYLE_BY_ID: (idType: string) => `/v1/general/hotspot-types/${idType}`,
        CREATE_NEW_HOTSPOT: (id_path: string) => `/v1/projects/hotspot?id_path=${id_path}`,
        CREATE_BULK_HOTSPOTS: (id_path: string) => `/v1/projects/hotspot/bulk?id_path=${id_path}`,
        UPDATE_HOTSPOT: (id_path: string, hotspot_id: number) => `/v1/projects/hotspot/${hotspot_id}?id_path=${id_path}`,
        DELETE_HOTSPOT: (id_path: string, hotspot_id: number) => `/v1/projects/hotspot/${hotspot_id}?id_path=${id_path}`,
        COPY_HOTSPOT: (id_path: string, hotspot_id: number) => `/v1/projects/hotspot/${hotspot_id}/copy?id_path=${id_path}`,
        EXPORT_EXCEL: (id_path: string) => `/v1/projects/hotspot-excel/export?id_path=${id_path}`,
        IMPORT_EXCEL: (id_path: string) => `/v1/projects/hotspot-excel/import?id_path=${id_path}`,
        // Hotspot Type CRUD (General - shared across all projects)
        CREATE_HOTSPOT_TYPE: () => `/v1/general/hotspot-types`,
        UPDATE_HOTSPOT_TYPE: (id: string) => `/v1/general/hotspot-types/${id}`,
        DELETE_HOTSPOT_TYPE: (id: string) => `/v1/general/hotspot-types/${id}`,
        UPLOAD_FILE: () => `/v1/general/multiple-file-upload`,
    },
    General: {
        GET_ALL_VARIABLE_SETTING_AND_VALUE: (id_path: string, category_id: number) => `/v1/projects/settings/categories/${category_id}/key-value?id_path=${id_path}`,
        GET_SETTING_VARIABLE: (id_path: string) => `/v1/projects/settings/variables-value?id_path=${id_path}`,
        GET_SETTING_VALUES_BY_UUIDS: (id_path: string, selectedSublink?: string, language_id?: string) => `/v1/projects/settings/values/by-uuids?id_path=${id_path}${language_id ? `&language_id=${language_id}` : ''}${selectedSublink ? `&link_id=${selectedSublink}` : ''}`,
        UPDATE_LIST_SETTING_VALUE: (id_path: string) => `/v1/projects/settings/multiple/values?id_path=${id_path}`,
        UPDATE_SETTING_VALUES_BY_UUID: (uuid: string, id_path: string) => `/v1/projects/settings/values/by-uuid/${uuid}?id_path=${id_path}`,
        SESSION_POLICY: {
            GET: "/v1/general/session-policy",
            UPDATE: "/v1/general/session-policy",
        },
    },
    Odoo: {
        GET_MENUS: (id_path: string) => `/v1/projects/odoo/menus?id_path=${id_path}`,
    },
    ColorManagement: {
        GET_COLOR_BY_VERSION: (id_path: string, version: number) => `/v1/projects/color/versions/${version}/details?id_path=${id_path}`,
        GET_ALL_GROUP_COLOR: (id_path: string) => `/v1/projects/color/groups?id_path=${id_path}`,
        GET_ALL_VARIABLE_COLOR_BY_GROUP: (id_path: string, group_id: number) => `/v1/projects/color/groups/${group_id}/variables?id_path=${id_path}`,
        GET_ALL_VERSION_COLOR: (id_path: string) => `/v1/projects/color/versions?id_path=${id_path}`,
        GET_VARIABLE_VALUE_COLOR: (id_path: string, version: number) => `/v1/projects/color/versions/${version}/key-values?id_path=${id_path}`,
        CREATE_COLOR_VERSION: (id_path: string) => `/v1/projects/color?id_path=${id_path}`,
        CREATE_COLOR_GROUP: (id_path: string) => `/v1/projects/color/groups?id_path=${id_path}`,
        CREATE_VARIABLE_COLOR: (id_path: string) => `/v1/projects/color/variables/with-value?id_path=${id_path}`,
        ADD_LIST_COLOR_VALUE: (id_path: string) => `/v1/projects/color/values?id_path=${id_path}`,
        UPDATE_COLOR_VERSION: (id_path: string, version: string) => `/v1/projects/color?id_path=${id_path}&version=${version}`,
        UPDATE_COLOR_GROUP: (id_path: string, groupId: number) => `/v1/projects/color/groups/${groupId}?id_path=${id_path}`,
        UPDATE_VALUE_COLOR_VARIABLE: (id_path: string, id: number) => `/v1/projects/color/value/${id}?id_path=${id_path}`,
        UPDATE_ALL_COLOR_VALUES: (id_path: string) => `/v1/projects/color/values?id_path=${id_path}`,
        GET_ALL_COLOR_VERSION: (id_path: string) => `/v1/projects/color/versions?id_path=${id_path}`,
        DELETE_VARIABLE: (id_path: string, variableId: number) => `/v1/projects/color/variables/${variableId}?id_path=${id_path}`,
        DELETE_GROUP: (id_path: string, groupId: number) => `/v1/projects/color/groups/${groupId}?id_path=${id_path}`,
        RESET_COLOR_TO_DEFAULT: (id_path: string) => `/v1/projects/color/reset-to-default?id_path=${id_path}`,
    },

    multimedia: {
        GET_ALL_MULTI_MEDIA_BY_SCENE_NAME: (sceneName: string, id_path: string, locale: string) => withIdPath(`v1/projects/multi-media/scene/${sceneName}?language_id=${locale}`, id_path),
        GET_ASSETS_BY_SCENE: (sceneId: string | number, id_path: string) => `/v1/projects/multi-media/assets/scene/${encodeURIComponent(sceneId)}?id_path=${id_path}&language_id=vn&asset_type=polygon`,
        CREATE_MULTIMEDIA: (id_path: string) => `/v1/projects/multi-media?id_path=${id_path}`,
        GET_MULTIMEDIA_LIST: (id_path: string) => `/v1/projects/multi-media?id_path=${id_path}`,
        GET_MULTIMEDIA_BY_ID: (id_path: string, id: number) => `/v1/projects/multi-media/${id}?id_path=${id_path}`,
        UPDATE_MULTIMEDIA: (id_path: string, id: string) => `/v1/projects/multi-media/${id}?id_path=${id_path}`,
        DELETE_MULTIMEDIA: (id_path: string, id: number) => `/v1/projects/multi-media/${id}?id_path=${id_path}`,
    },
    subLink: {
        GET_ALL_SUB_LINKS: (id_path: string) => `/v1/projects/sublink?id_path=${id_path}`,

        GET_ALL_SUB_LINK_WITH_DETAIL: (id_path: string, limit: number, page: number) => `/v1/projects/sublink/with-details?id_path=${id_path}&limit=${limit}&page=${page}`,
        GET_SUB_LINK_WITH_DETAIL_BY_ID: (id_path: string, id: number) => `/v1/projects/sublink/${id}/with-details?id_path=${id_path}`,
        GET_LINK_BY_NAME: (id_path: string, name: string) => `/v1/projects/sublink/by-name/${name}?id_path=${id_path}`,
        GET_META_BY_SUB_LINK: (id_path: string, sublink: number) => `/v1/projects/sublink/${sublink}/meta?id_path=${id_path}`,
        GET_DATA_BY_SUB_LINK_AND_TYPE: (id_path: string, sublink: number, type: string) => `/v1/projects/sublink/${sublink}/entities/${type}?id_path=${id_path}`,
        CREATE_SUB_LINK: (id_path: string) => `/v1/projects/sublink?id_path=${id_path}`,
        CREATE_SUBLINK_WITH_META: (id_path: string) => `/v1/projects/sublink/with-meta?id_path=${id_path}`,
        UPDATE_SUB_LINK: (id_path: string, id: number) => `/v1/projects/sublink/${id}?id_path=${id_path}`,
        UPDATE_META_SUBLINK: (id_path: string, id: number) => `/v1/projects/sublink/${id}/meta?id_path=${id_path}`,
        UPDATE_ENTITIES_OF_SUB_LINK: (id_path: string, id: number, entity_type: string) => `/v1/projects/sublink/${id}/entities/${entity_type}?id_path=${id_path}`,
        UPDATE_META_OF_SUB_LINK: (id_path: string, id: number) => `/v1/projects/sublink/${id}/meta?id_path=${id_path}`,
        DELETE_SUB_LINK: (id_path: string, id: number) => `/v1/projects/sublink/complete/${id}?id_path=${id_path}`,
        SEARCH_SUBLINK: (id_path: string, search: string) => `/v1/projects/sublink/search?id_path=${id_path}&search=${search}`,
        REMOVE_ENTITY_FROM_SUB_LINK: (id_path: string, sublink_id: number, entity_type: string) => `/v1/projects/sublink/${sublink_id}/entities/${entity_type}?id_path=${id_path}`,
        ADD_ENTITY_NESTED_TO_SUB_LINK: (id_path: string, sublink_id: number, entity_type: string) => `/v1/projects/sublink/${sublink_id}/entities/${entity_type}/nested?id_path=${id_path}`,
        CREATE_LINK_DETAILS: (id_path: string, link_id: number) =>
            `/v1/projects/sublink/${link_id}/details?id_path=${id_path}`,
        DELETE_ENTITIES_FROM_SUBLINK: (id_path: string, link_id: number, entity_type: string) =>
            `/v1/projects/sublink/${link_id}/entities/${entity_type}?id_path=${id_path}`,
    },
    asset: {
        GET_ALL_ASSET_TYPE: (id_path: string) => `/v1/projects/asset/types?id_path=${id_path}`,
        GET_ASSET_WITH_CATEGORY_BY_TYPE: (id_path: string, language_id: string, type: string) => `/v1/projects/asset/categories-with-assets/type/${type}?language_id=${language_id}&id_path=${id_path}`,
        GET_CATEGORY_AND_ASSET_WITH_TYPE: (id_path: string, language_id: string, level: number, parent_category_uuid: string, asset_type: number) => `/v1/projects/asset/type/${asset_type}/categories-or-assets?id_path=${id_path}&language_id=${language_id}&parent_category_uuid=${parent_category_uuid}&level=${level}`,
        GET_CHILD_ASSETS: (id_path: string, parent_category_uuid: string, asset_type: number = 0, level: number = 1, language_id: string = 'vn') =>
            `/v1/projects/asset/type/${asset_type}/categories-or-assets?id_path=${id_path}&parent_category_uuid=${parent_category_uuid}&level=${level}&language_id=${language_id}`,
        GET_FORM_ASSET_BY_TYPE: (id_path: string, type: string) => `/v1/projects/asset-form/form/${type}?id_path=${id_path}`,
        GET_ALL_ASSET_VARIABLES: (id_path: string, type: number, language_id: string, show_hidden: string) => `/v1/projects/asset-form/variables/${type}?id_path=${id_path}&language_id=${language_id}&show_hidden=${show_hidden}`,
        TOGGLE_ASSET_VARIABLE_VISIBILITY: (id_path: string, variableId: string | number) => `/v1/projects/asset-form/variables/${variableId}/visibility?id_path=${id_path}`,
        UPDATE_ASSET: (id_path: string, asset_uuid: string) => `/v1/projects/asset/${asset_uuid}?id_path=${id_path}`,
        DELETE_ASSET: (id_path: string, asset_uuid: string) => `/v1/projects/asset/${asset_uuid}?id_path=${id_path}`,
        CREATE_ASSET: (id_path: string) => `/v1/projects/asset?id_path=${id_path}`,
        SEARCH_ASSET: (id_path: string, search: string | undefined, type: string | undefined) => {
            let url = `/v1/projects/asset/search?id_path=${id_path}`;
            if (search && search.trim()) url += `&keyword=${encodeURIComponent(search.trim())}`;
            if (type) url += `&type_id=${type}`;
            return url;
        },
        SEARCH_UNIFIED: (id_path: string, name: string, parent_uuid?: string, language_id: string = 'vn') => {
            let url = `/v1/projects/search?id_path=${id_path}&name=${encodeURIComponent(name)}&language_id=${language_id}`;
            if (parent_uuid) url += `&parent_uuid=${parent_uuid}`;
            return url;
        },
        TRANSLATE_CATEGORY: (id_path: string) => `/v1/projects/asset/translate-category?id_path=${id_path}`,
        ESTIMATE_TRANSLATE_CATEGORY: (id_path: string) => `/v1/projects/asset/translate-category/estimate?id_path=${id_path}`,
    },

    assetTemplate: {
        LIST: (id_path: string, asset_type_id?: number, language_id: string = 'vn', include_fields: boolean = false) => {
            let url = `/v1/projects/asset-templates?id_path=${id_path}&language_id=${language_id}`;
            if (asset_type_id) url += `&asset_type_id=${asset_type_id}`;
            if (include_fields) url += `&include_fields=1`;
            return url;
        },
        DETAIL: (id_path: string, templateId: string | number, language_id: string = 'vn') =>
            `/v1/projects/asset-templates/${templateId}?id_path=${id_path}&language_id=${language_id}`,
        CREATE: (id_path: string, language_id: string = 'vn') =>
            `/v1/projects/asset-templates?id_path=${id_path}&language_id=${language_id}`,
        UPDATE: (id_path: string, templateId: string | number, language_id: string = 'vn') =>
            `/v1/projects/asset-templates/${templateId}?id_path=${id_path}&language_id=${language_id}`,
        DELETE: (id_path: string, templateId: string | number) =>
            `/v1/projects/asset-templates/${templateId}?id_path=${id_path}`,
    },

    customField: {
        // Get fields by type
        GET_BY_TYPE: (id_path: string, type: string, language_id: string = 'vn') =>
            `/v1/projects/asset-form/variables/${type}?id_path=${id_path}&language_id=${language_id}`,
        // Get single field by ID
        GET_BY_ID: (id_path: string, fieldId: string | number, language_id: string = 'vn') =>
            `/v1/projects/custom-fields/field/${fieldId}?id_path=${id_path}&language_id=${language_id}`,
        // Create new field
        INSERT: (id_path: string) => `/v1/projects/custom-fields/field?id_path=${id_path}`,
        // Update existing field
        UPDATE: (id_path: string, fieldId: string | number) => `/v1/projects/custom-fields/field/${fieldId}?id_path=${id_path}`,
        // Delete field
        DELETE: (id_path: string, fieldId: string | number) => `/v1/projects/custom-fields/field/${fieldId}?id_path=${id_path}`,
    },

    statistics: {
        GET_GROUP_STATISTICS: (id_path: string) => `/v1/projects/statistic-and-reports/statistic/group?id_path=${id_path}`,
        GET_SCENE_STATISTICS: (id_path: string) => `/v1/projects/statistic-and-reports/statistic/scene?id_path=${id_path}`,
        EXPORT_GROUP_REPORT: (id_path: string) => `/v1/projects/statistic-and-reports/export-report/group?id_path=${id_path}`,
        EXPORT_SCENE_REPORT: (id_path: string) => `/v1/projects/statistic-and-reports/export-report/scene?id_path=${id_path}`,
        // Asset Statistics
        GET_ASSET_OVERVIEW: (id_path: string) => `/v1/projects/asset-statistics/overview?id_path=${id_path}`,
        GET_ASSET_TIME_SERIES: (id_path: string) => `/v1/projects/asset-statistics/time-series?id_path=${id_path}`,
        GET_ASSET_LIST: (id_path: string) => `/v1/projects/asset-statistics/assets?id_path=${id_path}`,
        GET_ASSET_MOVEMENT: (id_path: string) => `/v1/projects/asset-statistics/movement?id_path=${id_path}`,
        GET_ASSET_SCENES: (id_path: string) => `/v1/projects/asset-statistics/scenes?id_path=${id_path}`,
        // Export with multiple formats
        EXPORT_ASSET_EXCEL: (id_path: string) => `/v1/projects/asset-statistics/export/excel?id_path=${id_path}`,
        EXPORT_ASSET_CSV: (id_path: string) => `/v1/projects/asset-statistics/export/csv?id_path=${id_path}`,
        EXPORT_ASSET_PDF: (id_path: string) => `/v1/projects/asset-statistics/export/pdf?id_path=${id_path}`,
    },

    module: {
        CREATE_MODULE: (id_path: string) => `/v1/projects/modules?id_path=${id_path}`,
        UPDATE_MODULE: (id_path: string, id: number) => `/v1/projects/modules/${id}?id_path=${id_path}`,
        DELETE_MODULE: (id_path: string, id: number) => `/v1/projects/modules/${id}?id_path=${id_path}`
    },

    customer: {
        GET_ALL: (id_path: string, page: number = 1, limit: number = 20, search: string = '', provider: string = '', status: string = '') => {
            let url = `/v1/projects/customers?id_path=${id_path}&page=${page}&limit=${limit}`;
            if (search) url += `&search=${encodeURIComponent(search)}`;
            if (provider) url += `&provider=${encodeURIComponent(provider)}`;
            if (status) url += `&status=${encodeURIComponent(status)}`;
            return url;
        },
        GET_BY_ID: (id_path: string, id: number | string) => `/v1/projects/customers/${id}?id_path=${id_path}`,
        UPDATE: (id_path: string, id: number | string) => `/v1/projects/customers/${id}?id_path=${id_path}`,
    },

    voice: {
        ADD_NEW_VOICES: (id_path: string) => `/v1/projects/voice/add-list-voice?id_path=${id_path}`,
        GET_VOICE_BY_ENTITY_TYPE_AND_ID: (id_path: string, entity_type: string, entity_id: string) =>
            `/v1/projects/voice/entity-type/${entity_type}/entity-id/${entity_id}?id_path=${id_path}`,
        GET_VOICE_BY_TYPE_ID_AND_LANGUAGE: (id_path: string, entity_type: string, entity_id: string, language_id: string) =>
            `/v1/projects/voice/entity-type/${entity_type}/entity-id/${entity_id}/language/${language_id}?id_path=${id_path}`,
        GET_VOICE_BY_TYPE_ID_LANGUAGE_AND_GENDER: (id_path: string, entity_type: string, entity_id: string, language_id: string, gender: 'nam' | 'nu') =>
            `/v1/projects/voice/entity-type/${entity_type}/entity-id/${entity_id}/language/${language_id}/gender/${gender}?id_path=${id_path}`,
    },

    // Survey Management
    survey: {
        // Survey CRUD
        GET_ALL_SURVEYS: (id_path: string, page: number = 1, limit: number = 10, search: string = '', is_active?: number) => {
            let url = `/v1/projects/survey?id_path=${id_path}&page=${page}&limit=${limit}`;
            if (search) url += `&search=${encodeURIComponent(search)}`;
            if (is_active !== undefined) url += `&is_active=${is_active}`;
            return url;
        },
        GET_SURVEY_BY_ID: (id_path: string, id: number, language_id: string = 'vi') =>
            `/v1/projects/survey/${id}?id_path=${id_path}&language_id=${language_id}`,
        CREATE_SURVEY: (id_path: string) => `/v1/projects/survey?id_path=${id_path}`,
        UPDATE_SURVEY: (id_path: string, id: number) => `/v1/projects/survey/${id}?id_path=${id_path}`,
        DELETE_SURVEY: (id_path: string, id: number) => `/v1/projects/survey/${id}?id_path=${id_path}`,
        DUPLICATE_SURVEY: (id_path: string, id: number) => `/v1/projects/survey/${id}/duplicate?id_path=${id_path}`,
        GET_SURVEY_STATISTICS: (id_path: string, id: number, link_id?: number) => {
            let url = `/v1/projects/survey/${id}/statistics?id_path=${id_path}`;
            if (link_id) url += `&link_id=${link_id}`;
            return url;
        },

        // Public Survey
        GET_PUBLIC_SURVEY_BY_CODE: (id_path: string, code: string, language_id: string = 'vi') =>
            `/v1/projects/survey/public/${code}?id_path=${id_path}&language_id=${language_id}`,
        GET_PUBLIC_SURVEY_BY_ID: (id_path: string, id: number, language_id: string = 'vi') =>
            `/v1/projects/survey/public/${id}?id_path=${id_path}&language_id=${language_id}`,

        // Question Management
        GET_QUESTIONS_BY_SURVEY_ID: (id_path: string, survey_id: number, language_id: string = 'vi') =>
            `/v1/projects/survey/${survey_id}/questions?id_path=${id_path}&language_id=${language_id}`,
        CREATE_QUESTION: (id_path: string, survey_id: number) =>
            `/v1/projects/survey/${survey_id}/questions?id_path=${id_path}`,
        UPDATE_QUESTION: (id_path: string, question_id: number) =>
            `/v1/projects/survey/questions/${question_id}?id_path=${id_path}`,
        DELETE_QUESTION: (id_path: string, question_id: number) =>
            `/v1/projects/survey/questions/${question_id}?id_path=${id_path}`,

        // Answer Options Management
        GET_ANSWER_OPTIONS_BY_QUESTION_ID: (id_path: string, question_id: number, language_id: string = 'vi') =>
            `/v1/projects/survey/questions/${question_id}/options?id_path=${id_path}&language_id=${language_id}`,
        CREATE_ANSWER_OPTION: (id_path: string, question_id: number) =>
            `/v1/projects/survey/questions/${question_id}/options?id_path=${id_path}`,
        UPDATE_ANSWER_OPTION: (id_path: string, option_id: number) =>
            `/v1/projects/survey/options/${option_id}?id_path=${id_path}`,
        DELETE_ANSWER_OPTION: (id_path: string, option_id: number) =>
            `/v1/projects/survey/options/${option_id}?id_path=${id_path}`,

        // Response Management
        SUBMIT_SURVEY_RESPONSE: (id_path: string, survey_id: number) =>
            `/v1/projects/survey/${survey_id}/responses?id_path=${id_path}`,
        GET_RESPONSES_BY_SURVEY_ID: (id_path: string, survey_id: number, page: number = 1, limit: number = 10, link_id?: number) => {
            let url = `/v1/projects/survey/${survey_id}/responses?id_path=${id_path}&page=${page}&limit=${limit}`;
            if (link_id) url += `&link_id=${link_id}`;
            return url;
        },
        GET_RESPONSE_DETAIL_BY_ID: (id_path: string, response_id: number, language_id: string = 'vi') =>
            `/v1/projects/survey/responses/${response_id}?id_path=${id_path}&language_id=${language_id}`,
        DELETE_RESPONSE: (id_path: string, response_id: number) =>
            `/v1/projects/survey/responses/${response_id}?id_path=${id_path}`,

        // Sublink Management (Apply Survey to Sublinks)
        GET_AVAILABLE_SUBLINKS: (id_path: string) =>
            `/v1/projects/survey/available-sublinks/all?id_path=${id_path}`,
        GET_APPLIED_SUBLINKS: (id_path: string, survey_id: number) =>
            `/v1/projects/survey/${survey_id}/sublinks?id_path=${id_path}`,
        APPLY_SUBLINK: (id_path: string, survey_id: number) =>
            `/v1/projects/survey/${survey_id}/sublinks?id_path=${id_path}`,
        REMOVE_SUBLINK: (id_path: string, survey_id: number, sublink_id: number) =>
            `/v1/projects/survey/${survey_id}/sublinks/${sublink_id}?id_path=${id_path}`,
        GET_SURVEY_BY_SUBLINK: (id_path: string, link_id: number, language_id: string = 'vi') =>
            `/v1/projects/survey/link/${link_id}?id_path=${id_path}&language_id=${language_id}`,
        GET_LINK_DETAILS_BY_ENTITY: (id_path: string, link_id: number, entity_type: string, entity_id?: number) =>
            `/v1/projects/sublink/${link_id}/entities/${entity_type}?id_path=${id_path}${entity_id ? `&entity_id=${entity_id}` : ''}`,
        GET_LINKS_BY_ENTITY: (id_path: string, entity_type: string, entity_id: number) =>
            `/v1/projects/sublink/entities/${entity_type}/${entity_id}?id_path=${id_path}`,

        // New Survey Apply APIs
        APPLY_SURVEY_TO_ENTITIES: (id_path: string) =>
            `/v1/projects/survey/apply?id_path=${id_path}`,
        REMOVE_SURVEY_FROM_ENTITIES: (id_path: string) =>
            `/v1/projects/survey/remove?id_path=${id_path}`,
        GET_ENTITIES_BY_SURVEY: (id_path: string, survey_id: number, entity_type?: string) => {
            let url = `/v1/projects/survey/${survey_id}/entities?id_path=${id_path}`;
            if (entity_type) url += `&entity_type=${entity_type}`;
            return url;
        },
        GET_SURVEYS_BY_ENTITY: (id_path: string, entity_type: string, entity_id: number) =>
            `/v1/projects/survey/entity/${entity_type}/${entity_id}?id_path=${id_path}`,
        GET_PUBLIC_SURVEY_BY_ENTITY: (id_path: string, entity_type: string, entity_id: number, language_id: string = 'vi') =>
            `/v1/projects/survey/public/entity/${entity_type}/${entity_id}?id_path=${id_path}&language_id=${language_id}`,

        // Survey Settings Management
        GET_SURVEY_SETTINGS: (id_path: string, survey_id?: number) => {
            let url = `/v1/projects/survey/settings?id_path=${id_path}`;
            if (survey_id) url += `&survey_id=${survey_id}`;
            return url;
        },
        CREATE_SURVEY_SETTINGS: (id_path: string, survey_id: number) =>
            `/v1/projects/survey/${survey_id}/settings?id_path=${id_path}`,
        UPDATE_SURVEY_SETTINGS: (id_path: string, survey_id: number) =>
            `/v1/projects/survey/${survey_id}/settings?id_path=${id_path}`,
    },

    minimapMarkerType: {
        GET_ALL: (id_path: string) => `/v1/projects/minimap-marker-type?id_path=${id_path}`,
        GET_BY_ID: (id_path: string, id: number) => `/v1/projects/minimap-marker-type/${id}?id_path=${id_path}`,
        CREATE: (id_path: string) => `/v1/projects/minimap-marker-type?id_path=${id_path}`,
        UPDATE: (id_path: string, id: number) => `/v1/projects/minimap-marker-type/${id}?id_path=${id_path}`,
        DELETE: (id_path: string, id: number) => `/v1/projects/minimap-marker-type/${id}?id_path=${id_path}`,
    },

    arNavigation: {
        GET_ALL_DESTINATIONS: (id_path: string, page: number, limit: number, search?: string, is_active?: number) => {
            let url = `/v1/projects/ar-navigation-destinations?id_path=${id_path}&page=${page}&limit=${limit}`;
            if (search) url += `&search=${encodeURIComponent(search)}`;
            if (is_active !== undefined) url += `&is_active=${is_active}`;
            return url;
        },
        GET_DESTINATION_BY_ID: (id_path: string, id: number) => `/v1/projects/ar-navigation-destinations/${id}?id_path=${id_path}`,
        CREATE_DESTINATION: (id_path: string) => `/v1/projects/ar-navigation-destinations?id_path=${id_path}`,
        UPDATE_DESTINATION: (id_path: string, id: number) => `/v1/projects/ar-navigation-destinations/${id}?id_path=${id_path}`,
        DELETE_DESTINATION: (id_path: string, id: number) => `/v1/projects/ar-navigation-destinations/${id}?id_path=${id_path}`,
    },
    // Project Management (General)
    projectManagement: {
        BACKUP_DATABASE: (projectId: string | number) => `/v1/general/projects/${projectId}/backup`,
        RESTORE_DATABASE: (projectId: string | number) => `/v1/general/projects/${projectId}/restore`,
        // Project-scoped endpoints (uses projects middleware with id_path permission check)
        BACKUP_DATABASE_PROJECT: (projectId: string | number, id_path?: string) => withIdPath(`/v1/projects/projects-management/${projectId}/backup`, id_path),
        RESTORE_DATABASE_PROJECT: (projectId: string | number, id_path?: string) => withIdPath(`/v1/projects/projects-management/${projectId}/restore`, id_path),
    },

    // Universal List System
    lists: {
        GET_ALL_LISTS: (id_path?: string, language_id?: string, scene_name?: string) => {
            let url = `/v1/projects/lists`;
            const params: string[] = [];
            if (language_id) params.push(`language_id=${language_id}`);
            if (scene_name) params.push(`scene_name=${encodeURIComponent(scene_name)}`);
            if (params.length > 0) url += `?${params.join('&')}`;
            return withIdPath(url, id_path);
        },
        GET_LIST_BY_ID: (id: string, id_path?: string) => withIdPath(`/v1/projects/lists/${id}`, id_path),
        CREATE_LIST: (id_path?: string) => withIdPath(`/v1/projects/lists`, id_path),
        UPDATE_LIST: (id: string, id_path?: string) => withIdPath(`/v1/projects/lists/${id}`, id_path),
        DELETE_LIST: (id: string, id_path?: string) => withIdPath(`/v1/projects/lists/${id}`, id_path),
    },

    // Agent Config Management (per project)
    agentConfig: {
        GET: (id_path: string) => `/v1/projects/agent-config?id_path=${id_path}`,
        UPDATE: (id_path: string) => `/v1/projects/agent-config?id_path=${id_path}`,
        DELETE: (id_path: string) => `/v1/projects/agent-config?id_path=${id_path}`,
    },

    // Tour Mode Config Management (per project dedicated table sg3d_tour_mode_config)
    tourModeConfig: {
        GET: (id_path: string, link_id: number | string = 1) => `/v1/projects/tour-mode-config?id_path=${id_path}&link_id=${link_id}`,
        GET_PUBLIC: (id_path: string, link_id: number | string = 1) => `/v1/projects/tour-mode-config/public?id_path=${id_path}&link_id=${link_id}`,
        UPDATE: (id_path: string, link_id: number | string = 1) => `/v1/projects/tour-mode-config?id_path=${id_path}&link_id=${link_id}`,
    },

    // Agent 3D Models (master + project assignment)
    agent3dModels: {
        LIST: (id_path: string, page = 1, limit = 20, search = '', is_active?: boolean) => {
            let url = `/v1/projects/agent-3d-models?id_path=${id_path}&page=${page}&limit=${limit}`;
            if (search) url += `&search=${encodeURIComponent(search)}`;
            if (typeof is_active === 'boolean') url += `&is_active=${is_active}`;
            return url;
        },
        DETAIL: (id_path: string, id: string | number) => `/v1/projects/agent-3d-models/${id}?id_path=${id_path}`,
        CREATE: (id_path: string) => `/v1/projects/agent-3d-models?id_path=${id_path}`,
        UPDATE: (id_path: string, id: string | number) => `/v1/projects/agent-3d-models/${id}?id_path=${id_path}`,
        DELETE: (id_path: string, id: string | number, scope: 'project' | 'global' = 'project') =>
            `/v1/projects/agent-3d-models/${id}?id_path=${id_path}&scope=${scope}`,
        GET_ASSIGNMENTS: (id: string | number) => `/v1/projects/agent-3d-models/${id}/assignments`,
        ASSIGN: (id: string | number) => `/v1/projects/agent-3d-models/${id}/assignments`,
        UNASSIGN: (id: string | number, projectId: string | number) => `/v1/projects/agent-3d-models/${id}/assignments/${projectId}`,
    },

    // Embed Key Admin CRUD — dùng path param (không dùng ?id_path=)
    embedKey: {
        LIST: (idPath: string, namePath?: string) =>
            `/v1/projects/${idPath}/embed-keys${namePath ? `?name_path=${encodeURIComponent(namePath)}` : ''}`,
        GET_BY_ID: (idPath: string, tokenId: number, namePath?: string) =>
            `/v1/projects/${idPath}/embed-keys/${tokenId}${namePath ? `?name_path=${encodeURIComponent(namePath)}` : ''}`,
        CREATE: (idPath: string) => `/v1/projects/${idPath}/embed-keys`,
        UPDATE: (idPath: string, tokenId: number) => `/v1/projects/${idPath}/embed-keys/${tokenId}`,
        DELETE: (idPath: string, tokenId: number) => `/v1/projects/${idPath}/embed-keys/${tokenId}`,
        SET_DOMAIN: (idPath: string, tokenId: number) => `/v1/projects/${idPath}/embed-keys/${tokenId}/domain`,
        DEL_DOMAIN: (idPath: string, tokenId: number) => `/v1/projects/${idPath}/embed-keys/${tokenId}/domain`,
    },

    // Plausible Analytics iframe
    plausibleAnalytics: {
        GET_IFRAME_URL: (idPath: string) => `/v1/projects/plausible-iframe?id_path=${encodeURIComponent(idPath)}`,
    },

    // Asset Loan Management
    assetLoan: {
        LIST_LOAN_REQUESTS: (id_path: string) => `/v1/projects/asset-loans?id_path=${id_path}`,
        GET_LOAN_REQUEST_BY_ID: (id_path: string, loanId: number) => `/v1/projects/asset-loans/${loanId}?id_path=${id_path}`,
        GET_PUBLIC_LOAN_REQUEST: (id_path: string, loanId: number) => `/v1/projects/asset-loans/${loanId}/public?id_path=${id_path}`,
        CREATE_LOAN_REQUEST: (id_path: string) => `/v1/projects/asset-loans?id_path=${id_path}`,
        UPDATE_LOAN_REQUEST: (id_path: string, loanId: number) => `/v1/projects/asset-loans/${loanId}?id_path=${id_path}`,
        DELETE_LOAN_REQUEST: (id_path: string, loanId: number) => `/v1/projects/asset-loans/${loanId}?id_path=${id_path}`,
        SUBMIT_LOAN_REQUEST: (id_path: string, loanId: number) => `/v1/projects/asset-loans/${loanId}/submit?id_path=${id_path}`,
        APPROVE_LOAN_REQUEST: (id_path: string, loanId: number) => `/v1/projects/asset-loans/${loanId}/approve?id_path=${id_path}`,
        REJECT_LOAN_REQUEST: (id_path: string, loanId: number) => `/v1/projects/asset-loans/${loanId}/reject?id_path=${id_path}`,
        MARK_AS_BORROWED: (id_path: string, loanId: number) => `/v1/projects/asset-loans/${loanId}/borrow?id_path=${id_path}`,
        MARK_AS_RETURNED: (id_path: string, loanId: number) => `/v1/projects/asset-loans/${loanId}/return?id_path=${id_path}`,
        REQUEST_RETURN: (id_path: string, loanId: number) => `/v1/projects/asset-loans/${loanId}/request-return?id_path=${id_path}`,
        EXTEND_LOAN: (id_path: string, loanId: number) => `/v1/projects/asset-loans/${loanId}/extend?id_path=${id_path}`,
        GET_LOAN_ITEMS: (id_path: string, loanId: number) => `/v1/projects/asset-loans/${loanId}/items?id_path=${id_path}`,
        ADD_LOAN_ITEM: (id_path: string, loanId: number) => `/v1/projects/asset-loans/${loanId}/items?id_path=${id_path}`,
        UPDATE_LOAN_ITEM: (id_path: string, itemId: number) => `/v1/projects/asset-loans/items/${itemId}?id_path=${id_path}`,
        REMOVE_LOAN_ITEM: (id_path: string, itemId: number) => `/v1/projects/asset-loans/items/${itemId}?id_path=${id_path}`,
        APPROVE_LOAN_ITEM: (id_path: string, itemId: number) => `/v1/projects/asset-loans/items/${itemId}/approve?id_path=${id_path}`,
        REJECT_LOAN_ITEM: (id_path: string, itemId: number) => `/v1/projects/asset-loans/items/${itemId}/reject?id_path=${id_path}`,
        MY_PENDING_APPROVALS: (id_path: string) => `/v1/projects/asset-loans/my/pending?id_path=${id_path}`,
        MY_PENDING_APPROVALS_DETAILED: (id_path: string) => `/v1/projects/asset-loans/my/pending-approvals?id_path=${id_path}`,
        MY_LOAN_REQUESTS: (id_path: string) => `/v1/projects/asset-loans/my/requests?id_path=${id_path}`,
        GET_OVERDUE_LOANS: (id_path: string) => `/v1/projects/asset-loans/overdue?id_path=${id_path}`,
        GET_LOAN_STATISTICS: (id_path: string) => `/v1/projects/asset-loans/statistics?id_path=${id_path}`,
        GET_LOANS_BY_ASSET: (id_path: string, uuid: string) => `/v1/projects/asset-loans/asset/${uuid}?id_path=${id_path}`,
        // Multi-level approval workflow endpoints
        GET_WORKFLOW_STEPS: (id_path: string, loanId: number) => `/v1/projects/asset-loans/${loanId}/workflow-steps?id_path=${id_path}`,
        GET_APPROVAL_PROGRESS: (id_path: string, loanId: number) => `/v1/projects/asset-loans/${loanId}/approval-progress?id_path=${id_path}`,
        APPROVE_AT_LEVEL: (id_path: string, loanId: number, level: number) => `/v1/projects/asset-loans/${loanId}/approve-level/${level}?id_path=${id_path}`,
        REJECT_AT_LEVEL: (id_path: string, loanId: number, level: number) => `/v1/projects/asset-loans/${loanId}/reject-level/${level}?id_path=${id_path}`,
        APPROVE_ITEMS: (id_path: string, loanId: number) => `/v1/projects/asset-loans/${loanId}/approve-items?id_path=${id_path}`,
        REJECT_ITEMS: (id_path: string, loanId: number) => `/v1/projects/asset-loans/${loanId}/reject-items?id_path=${id_path}`,
        DELEGATE_APPROVAL: (id_path: string, loanId: number) => `/v1/projects/asset-loans/${loanId}/delegate?id_path=${id_path}`,
        // Approver Management
        ADD_APPROVER: (id_path: string, loanId: number) => `/v1/projects/asset-loans/${loanId}/approvers?id_path=${id_path}`,
        UPDATE_APPROVER: (id_path: string, loanId: number, stepId: number) => `/v1/projects/asset-loans/${loanId}/approvers/${stepId}?id_path=${id_path}`,
        REMOVE_APPROVER: (id_path: string, loanId: number, stepId: number) => `/v1/projects/asset-loans/${loanId}/approvers/${stepId}?id_path=${id_path}`,
        REORDER_APPROVERS: (id_path: string, loanId: number) => `/v1/projects/asset-loans/${loanId}/approvers/reorder?id_path=${id_path}`,
        GET_QUANTITY_SUMMARY: (id_path: string, loanId: number) => `/v1/projects/asset-loans/${loanId}/quantity-summary?id_path=${id_path}`,
        UPDATE_QUANTITIES: (id_path: string, loanId: number) => `/v1/projects/asset-loans/${loanId}/update-quantities?id_path=${id_path}`,
        PARTIAL_RETURN: (id_path: string, loanId: number) => `/v1/projects/asset-loans/${loanId}/partial-return?id_path=${id_path}`,
        FINALIZE_RETURN: (id_path: string, loanId: number) => `/v1/projects/asset-loans/${loanId}/finalize-return?id_path=${id_path}`,
        GET_ITEM_APPROVAL_STATUS: (id_path: string, loanId: number) => `/v1/projects/asset-loans/${loanId}/item-approval-status?id_path=${id_path}`,
        // Handover endpoints
        GET_ALL_HANDOVER_RECORDS: (id_path: string) => `/v1/projects/asset-loans/handover-records?id_path=${id_path}`,
        GET_HANDOVER_BY_ID: (id_path: string, handoverId: number) => `/v1/projects/asset-loans/handover-records/${handoverId}?id_path=${id_path}`,
        GET_HANDOVER_ITEMS: (id_path: string, handoverId: number) => `/v1/projects/asset-loans/handover-records/${handoverId}/items?id_path=${id_path}`,
        CREATE_HANDOVER: (id_path: string, loanId: number) => `/v1/projects/asset-loans/${loanId}/create-handover?id_path=${id_path}`,
        CONFIRM_HANDOVER: (id_path: string, handoverId: number) => `/v1/projects/asset-loans/handover-records/${handoverId}/confirm?id_path=${id_path}`,
        REJECT_HANDOVER: (id_path: string, handoverId: number) => `/v1/projects/asset-loans/handover-records/${handoverId}/reject?id_path=${id_path}`,
        GET_HANDOFFERS_BY_LOAN: (id_path: string, loanId: number) => `/v1/projects/asset-loans/handover-records/loan/${loanId}?id_path=${id_path}`,
    },

    // Asset Handover Management
    assetHandover: {
        LIST_HANDOVER_RECORDS: (id_path: string) => `/v1/projects/asset-handoffs?id_path=${id_path}`,
        GET_HANDOVER_BY_ID: (id_path: string, handoverId: number) => `/v1/projects/asset-handoffs/${handoverId}?id_path=${id_path}`,
        CREATE_HANDOVER_RECORD: (id_path: string) => `/v1/projects/asset-handoffs?id_path=${id_path}`,
        UPDATE_HANDOVER_RECORD: (id_path: string, handoverId: number) => `/v1/projects/asset-handoffs/${handoverId}?id_path=${id_path}`,
        DELETE_HANDOVER_RECORD: (id_path: string, handoverId: number) => `/v1/projects/asset-handoffs/${handoverId}?id_path=${id_path}`,
        CONFIRM_HANDOVER: (id_path: string, handoverId: number) => `/v1/projects/asset-handoffs/${handoverId}/confirm?id_path=${id_path}`,
        REJECT_HANDOVER: (id_path: string, handoverId: number) => `/v1/projects/asset-handoffs/${handoverId}/reject?id_path=${id_path}`,
        GET_HANDOVER_ITEMS: (id_path: string, handoverId: number) => `/v1/projects/asset-handoffs/${handoverId}/items?id_path=${id_path}`,
        ADD_HANDOVER_ITEM: (id_path: string, handoverId: number) => `/v1/projects/asset-handoffs/${handoverId}/items?id_path=${id_path}`,
        GET_HANDOFFERS_BY_LOAN: (id_path: string, loanId: number) => `/v1/projects/asset-handoffs/loan/${loanId}?id_path=${id_path}`,
        GET_HANDOVER_HISTORY: (id_path: string, assetUuid: string) => `/v1/projects/asset-handoffs/asset/${assetUuid}?id_path=${id_path}`,
    },

    // Cart Management for Asset Loans
    cart: {
        LIST_CART: (id_path: string) => `/v1/projects/cart?id_path=${id_path}`,
        ADD_ITEM: (id_path: string) => `/v1/projects/cart/items?id_path=${id_path}`,
        UPDATE_ITEM: (id_path: string, cartId: number) => `/v1/projects/cart/items/${cartId}?id_path=${id_path}`,
        REMOVE_ITEM: (id_path: string, cartId: number) => `/v1/projects/cart/items/${cartId}?id_path=${id_path}`,
        CLEAR_CART: (id_path: string) => `/v1/projects/cart?id_path=${id_path}`,
    },

    // Display Management - Display-only hierarchy config for assets
    displayConfig: {
        GET_BY_ASSET_TYPE: (id_path: string, assetTypeId: number) =>
            `/v1/projects/display-config?assetTypeId=${assetTypeId}&id_path=${encodeURIComponent(id_path)}`,
        GET_HIERARCHY_FOR_HERITAGE: (id_path: string, assetTypeId: number) =>
            `/v1/projects/display-config/heritage-hierarchy/${assetTypeId}?id_path=${encodeURIComponent(id_path)}`,
        GET_CHILDREN_FOR_HERITAGE: (id_path: string, assetTypeId: number, parentUuid: string | null, language_id: string = 'vn') =>
            `/v1/projects/display-config/heritage-children/${parentUuid || 'root'}?id_path=${encodeURIComponent(id_path)}&assetTypeId=${assetTypeId}&language_id=${language_id}`,
        CREATE: (id_path: string) =>
            `/v1/projects/display-config?id_path=${encodeURIComponent(id_path)}`,
        CREATE_BATCH: (id_path: string) =>
            `/v1/projects/display-config/batch?id_path=${encodeURIComponent(id_path)}`,
        DELETE_BATCH: (id_path: string) =>
            `/v1/projects/display-config/batch?id_path=${encodeURIComponent(id_path)}`,
        MOVE_BATCH: (id_path: string) =>
            `/v1/projects/display-config/move-batch?id_path=${encodeURIComponent(id_path)}`,
        DESCENDANT_COUNTS: (id_path: string) =>
            `/v1/projects/display-config/descendant-counts?id_path=${encodeURIComponent(id_path)}`,
        UPDATE: (id_path: string, uuid: string) =>
            `/v1/projects/display-config/${uuid}?id_path=${encodeURIComponent(id_path)}`,
        DELETE: (id_path: string, uuid: string) =>
            `/v1/projects/display-config/${uuid}?id_path=${encodeURIComponent(id_path)}`,
        GET_AVAILABLE_ASSETS: (id_path: string, assetTypeId: number, excludeDisplayConfig = true, page = 1, limit = 20) =>
            `/v1/projects/asset/available?id_path=${encodeURIComponent(id_path)}&assetTypeId=${assetTypeId}&excludeDisplayConfig=${excludeDisplayConfig}&page=${page}&limit=${limit}`,
        GET_ALL_FOLDERS: (id_path: string) =>
            `/v1/projects/display-config/all-folders?id_path=${encodeURIComponent(id_path)}`,
        CHECK_EXISTS: (id_path: string, assetTypeId: number) =>
            `/v1/projects/display-config/exists/${assetTypeId}?id_path=${encodeURIComponent(id_path)}`,
        GET_CHILDREN: (id_path: string, assetTypeId: number, parentUuid: string | null) =>
            `/v1/projects/display-config/children/${parentUuid || 'root'}?id_path=${encodeURIComponent(id_path)}&assetTypeId=${assetTypeId}`,
        GET_HERITAGE_INIT: (id_path: string, assetTypeId: number, language_id: string = 'vn') =>
            `/v1/projects/display-config/heritage-init/${assetTypeId}?id_path=${encodeURIComponent(id_path)}&language_id=${language_id}`,
        GET_ASSET_PARENT: (id_path: string, assetUuid: string) =>
            `/v1/projects/display-config/asset-parent/${assetUuid}?id_path=${encodeURIComponent(id_path)}`,
        GET_ASSET_PATH: (id_path: string, assetUuid: string) =>
            `/v1/projects/display-config/asset-path/${assetUuid}?id_path=${encodeURIComponent(id_path)}`,
        GET_CATEGORY_TREE: (id_path: string, language_id: string = 'vn') =>
            `/v1/projects/display-config/category-tree?id_path=${encodeURIComponent(id_path)}&language_id=${language_id}`,
        CREATE_WITH_CATEGORIES: (id_path: string) =>
            `/v1/projects/display-config/with-categories?id_path=${encodeURIComponent(id_path)}`,
        SEARCH: (id_path: string, q: string) =>
            `/v1/projects/display-config/search?id_path=${encodeURIComponent(id_path)}&q=${encodeURIComponent(q)}`,
        GET_STATISTICS: (id_path: string) =>
            `/v1/projects/display-config/statistics/summary?id_path=${encodeURIComponent(id_path)}`,
    },

    // Comments Management
    comments: {
        GET_ALL_COMMENTS: (id_path: string, page: number = 1, limit: number = 20) =>
            `/v1/projects/comments?id_path=${id_path}&page=${page}&limit=${limit}`,
        GET_COMMENTS_BY_OBJECT: (id_path: string, objectId: string, commentObjectTypeId: number) =>
            `/v1/projects/comments/object/${objectId}/${commentObjectTypeId}?id_path=${id_path}`,
        GET_COMMENT_COUNT: (id_path: string, objectId: string, commentObjectTypeId: number) =>
            `/v1/projects/comments/count/${objectId}/${commentObjectTypeId}?id_path=${id_path}`,
        GET_COMMENT_BY_ID: (id_path: string, id: number) =>
            `/v1/projects/comments/${id}?id_path=${id_path}`,
        CREATE_COMMENT: (id_path: string) =>
            `/v1/projects/comments?id_path=${id_path}`,
        UPDATE_COMMENT: (id_path: string, id: number) =>
            `/v1/projects/comments/${id}?id_path=${id_path}`,
        DELETE_COMMENT: (id_path: string, id: number) =>
            `/v1/projects/comments/${id}?id_path=${id_path}`,
        GET_OBJECT_TYPES: (id_path: string) =>
            `/v1/projects/comments/object-types?id_path=${id_path}`,
        CREATE_OBJECT_TYPE: (id_path: string) =>
            `/v1/projects/comments/object-types?id_path=${id_path}`,
    },

    // Settings Management
    settings: {
        GET_ALL_CATEGORIES: (id_path: string) =>
            `/v1/projects/settings/categories?id_path=${id_path}`,
        GET_CATEGORY_BY_ID: (id_path: string, categoryId: number) =>
            `/v1/projects/settings/categories/${categoryId}?id_path=${id_path}`,
        CREATE_CATEGORY: (id_path: string) =>
            `/v1/projects/settings/categories?id_path=${id_path}`,
        UPDATE_CATEGORY: (id_path: string, categoryId: number) =>
            `/v1/projects/settings/categories/${categoryId}?id_path=${id_path}`,
        DELETE_CATEGORY: (id_path: string, categoryId: number) =>
            `/v1/projects/settings/categories/${categoryId}?id_path=${id_path}`,
        GET_VARIABLES_BY_CATEGORY: (id_path: string, categoryId: number) =>
            `/v1/projects/settings/categories/${categoryId}/with-nested?id_path=${id_path}`,
        GET_KEY_VALUES_BY_CATEGORY: (id_path: string, categoryId: number) =>
            `/v1/projects/settings/categories/${categoryId}/key-value?id_path=${id_path}`,
        GET_VARIABLES_WITH_VALUES: (id_path: string, search?: string, linkId?: number | string) =>
            `/v1/projects/settings/variables/with-values?id_path=${id_path}&limit=1000${search ? `&search=${encodeURIComponent(search)}` : ''}${linkId ? `&link_id=${linkId}` : ''}`,
        CREATE_VARIABLE: (id_path: string) =>
            `/v1/projects/settings/variables?id_path=${id_path}`,
        UPDATE_VARIABLE: (id_path: string, variableId: number) =>
            `/v1/projects/settings/variables/${variableId}?id_path=${id_path}`,
        DELETE_VARIABLE: (id_path: string, variableId: number) =>
            `/v1/projects/settings/variables/${variableId}?id_path=${id_path}`,
        UPDATE_MULTIPLE_VALUES: (id_path: string) =>
            `/v1/projects/settings/multiple/values?id_path=${id_path}`,
    },

    // Label/Tag Management
    label: {
        GET_LABELS: (id_path: string, page = 1, pageSize = 50) =>
            `/v1/projects/label?id_path=${id_path}&page=${page}&pageSize=${pageSize}`,
        GET_LABELS_OF_OBJECT: (id_path: string, objectType: string, objectId: string) =>
            `/v1/projects/object-label/${objectType}/${objectId}/labels?id_path=${id_path}`,
        GET_LABELS_OF_OBJECTS: (id_path: string) =>
            `/v1/projects/label-objects/objects/labels?id_path=${id_path}`,
        CREATE_LABEL: (id_path: string) =>
            `/v1/projects/label?id_path=${id_path}`,
        UPDATE_LABEL: (id_path: string, labelId: string) =>
            `/v1/projects/label/${labelId}?id_path=${id_path}`,
        LABEL_OBJECT: (id_path: string) =>
            `/v1/projects/object-label?id_path=${id_path}`,
        UNLABEL_OBJECT: (id_path: string, objectType: string, objectId: string, labelId: string) =>
            `/v1/projects/object-label/${objectType}/${objectId}/${labelId}?id_path=${id_path}`,
        DELETE_LABEL: (id_path: string, labelId: string) =>
            `/v1/projects/label/${labelId}?id_path=${id_path}`,
    },

    // Filter/Advanced Search
    filter: {
        GET_FILTER_FIELDS: (id_path: string, assetTypeId?: number | null) =>
            `/v1/projects/filters/resource-management/fields?id_path=${encodeURIComponent(id_path)}${assetTypeId ? `&asset_type_id=${assetTypeId}` : ''}`,
        GET_FIELD_OPTIONS: (id_path: string, assetTypeId: number | null | undefined, fieldName: string) =>
            `/v1/projects/filters/resource-management/${encodeURIComponent(fieldName)}/options?id_path=${encodeURIComponent(id_path)}${assetTypeId ? `&asset_type_id=${assetTypeId}` : ''}`,
        SEARCH_ASSETS: (id_path: string) =>
            `/v1/projects/filters/resource-management/search?id_path=${encodeURIComponent(id_path)}`,
        GET_SAVED_FILTERS: (id_path: string, assetTypeId?: number | null) =>
            `/v1/projects/filters/resource-management/saved?id_path=${encodeURIComponent(id_path)}${assetTypeId ? `&asset_type_id=${assetTypeId}` : ''}`,
        GET_DEFAULT_SAVED_FILTER: (id_path: string) =>
            `/v1/projects/filters/resource-management/saved/default?id_path=${encodeURIComponent(id_path)}`,
        SAVE_SAVED_FILTER: (id_path: string) =>
            `/v1/projects/filters/resource-management/saved?id_path=${encodeURIComponent(id_path)}`,
        UPDATE_SAVED_FILTER: (id_path: string, filterId: string) =>
            `/v1/projects/filters/resource-management/saved/${encodeURIComponent(filterId)}?id_path=${encodeURIComponent(id_path)}`,
        DELETE_SAVED_FILTER: (id_path: string, filterId: string) =>
            `/v1/projects/filters/resource-management/saved/${encodeURIComponent(filterId)}?id_path=${encodeURIComponent(id_path)}`,
    },

    // Export APIs
    export: {
        EXPORT_ARTIFACT_CUSTOM_FIELD: (id_path: string, assetId: string, language_id?: string) =>
            `/v1/projects/export/artifact/custom-field?assetId=${assetId}&id_path=${id_path}${language_id ? `&language_id=${language_id}` : ''}`,
        EXPORT_ARTIFACTS_MULTIPLE: (id_path: string) =>
            `/v1/projects/export/artifacts?action=multiple&id_path=${id_path}`,
        GET_ARTIFACT_DETAIL_FOR_EXPORT: (id_path: string, assetId: string, language_id?: string) =>
            `/v1/projects/export/artifact/detail/${assetId}?id_path=${id_path}${language_id ? `&language_id=${language_id}` : ''}`,
        GET_FILE_FROM_OTHER_HOST: (url: string) =>
            `/v1/projects/export/file/external?url=${encodeURIComponent(url)}`,
    },

    // Point cloud dataset APIs (Potree integration)
    pointCloud: {
        LIST: (id_path: string) => `/v1/projects/${encodeURIComponent(id_path)}/point-clouds`,
        CREATE: (id_path: string) => `/v1/projects/${encodeURIComponent(id_path)}/point-clouds`,
        DELETE: (id_path: string, pcId: string) =>
            `/v1/projects/${encodeURIComponent(id_path)}/point-clouds/${encodeURIComponent(pcId)}`,
        RETRY: (id_path: string, pcId: string) =>
            `/v1/projects/${encodeURIComponent(id_path)}/point-clouds/${encodeURIComponent(pcId)}/retry`,
    },

    // Batch Voice Generation & Approval (PRD-2026-007)
    voiceBatch: {
        METADATA: (id_path: string) => withIdPath('/v1/projects/voice-batch/metadata', id_path),
        PREVIEW: (id_path: string) => withIdPath('/v1/projects/voice-batch/preview', id_path),
        SAMPLE: (id_path: string) => withIdPath('/v1/projects/voice-batch/sample', id_path),
        CONFIRM_SAMPLE: (id_path: string) => withIdPath('/v1/projects/voice-batch/confirm-sample', id_path),
        JOBS: (id_path: string) => withIdPath('/v1/projects/voice-batch/jobs', id_path),
        JOB_DETAIL: (id_path: string, jobId: string) =>
            withIdPath(`/v1/projects/voice-batch/jobs/${encodeURIComponent(jobId)}`, id_path),
        JOB_ITEMS: (id_path: string, jobId: string) =>
            withIdPath(`/v1/projects/voice-batch/jobs/${encodeURIComponent(jobId)}/items`, id_path),
        ITEM_DETAIL: (id_path: string, jobId: string, itemId: number | string) =>
            withIdPath(`/v1/projects/voice-batch/jobs/${encodeURIComponent(jobId)}/items/${encodeURIComponent(String(itemId))}`, id_path),
        PAUSE_JOB: (id_path: string, jobId: string) =>
            withIdPath(`/v1/projects/voice-batch/jobs/${encodeURIComponent(jobId)}/pause`, id_path),
        RESUME_JOB: (id_path: string, jobId: string) =>
            withIdPath(`/v1/projects/voice-batch/jobs/${encodeURIComponent(jobId)}/resume`, id_path),
        CANCEL_JOB: (id_path: string, jobId: string) =>
            withIdPath(`/v1/projects/voice-batch/jobs/${encodeURIComponent(jobId)}/cancel`, id_path),
        RETRY_ITEMS: (id_path: string, jobId: string) =>
            withIdPath(`/v1/projects/voice-batch/jobs/${encodeURIComponent(jobId)}/retry`, id_path),
        REVIEW_ITEMS: (id_path: string, jobId: string) =>
            withIdPath(`/v1/projects/voice-batch/jobs/${encodeURIComponent(jobId)}/review`, id_path),
        PREPARE_APPLY: (id_path: string, jobId: string) =>
            withIdPath('/v1/projects/voice-batch/jobs/' + encodeURIComponent(jobId) + '/prepare-apply', id_path),
        APPLY_ITEMS: (id_path: string, jobId: string) =>
            withIdPath(`/v1/projects/voice-batch/jobs/${encodeURIComponent(jobId)}/apply`, id_path),
        ROLLBACK_ITEMS: (id_path: string, jobId: string) =>
            withIdPath(`/v1/projects/voice-batch/jobs/${encodeURIComponent(jobId)}/rollback`, id_path),
        KILL_SWITCH: (id_path: string, jobId: string) =>
            withIdPath(`/v1/projects/voice-batch/jobs/${encodeURIComponent(jobId)}/kill-switch`, id_path),
        ENTITY_HISTORY: (id_path: string, entityType: string, entityId: string) =>
            withIdPath(`/v1/projects/voice-batch/history/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`, id_path),
    },
};


export default apiEndPointList;
